import {
  MAIN_FRAME_SLOT_ID,
  resolveMainFramePageSlotId,
  stampMainFramePageData,
} from '@/common/utils/mainFrameSlot'
import { extractFormFid } from '@/stores/app'
import { normalizeDialogPageUniDataFieldAliases } from '@/common/utils/cloneTableValue'
import { parsePlatformActionForm } from '@/services/core/core.service'
import { formHasRenderableLayout, formLayoutHasStubPanels } from '@/services/parse/parse.service'
import type { AppStore } from '@/stores/app'
import type { ActionDependencies, ExchangeData } from '@/services/action/types'

export type MainFrameMountOptions = {
  isStartupAction?: boolean
  openedFormId?: string
}

export type MainFrameBuildOptions = {
  formIdHint?: string
  platform?: string
  isStartupAction?: boolean
  appStore?: AppStore
  dependencies?: ActionDependencies
  invokeNestedElementActionFromClientScript?: (params: any) => Promise<ExchangeData | null>
}

export async function prepareMainFrameDocumentData(
  documentData: Record<string, unknown>,
  options?: MainFrameMountOptions,
): Promise<Record<string, unknown>> {
  await parsePlatformActionForm(documentData, -1)
  normalizeDialogPageUniDataFieldAliases(documentData)

  const stamped = stampMainFramePageData(documentData, {
    isStartupAction: options?.isStartupAction,
  })
  if (options?.openedFormId) {
    stamped.formId = options.openedFormId
  }
  return stamped
}

export function resolveMainFrameFormId(documentData: Record<string, unknown>): string {
  const fid = extractFormFid(documentData)
  if (fid) {
    return fid
  }
  const stack = Array.isArray(documentData.stack) ? documentData.stack : []
  if (stack.length > 0) {
    return String(stack[0])
  }
  const status = Number(documentData.status)
  if (Number.isFinite(status) && status > 0) {
    return String(status)
  }
  const form = documentData.form as Record<string, unknown> | undefined
  const attrs = form?._attributes as { id?: string } | undefined
  if (attrs?.id) {
    return String(attrs.id)
  }
  return `main_frame_${Date.now()}`
}

/** Парсинг XML/JSON формы и formId на панелях (startup dialog, MAIN_FRAME open). */
export async function applyActionResponseFormLayout(
  documentData: Record<string, unknown>,
  formId: string,
): Promise<void> {
  const rawXml = typeof documentData.form === 'string' ? documentData.form.trim() : ''
  await parsePlatformActionForm(documentData, formId)

  if (!documentData.form) {
    return
  }

  const numericId = parseInt(String(formId), 10)
  const schemaId = Number.isFinite(numericId) && numericId > 0 ? numericId : 0

  const applyParsedForm = async (formSource: unknown) => {
    const { parseService } = await import('@/services/parse/parse.service')
    documentData.form = parseService.getForm(formSource, schemaId)
    stampFormIdOnPanels(documentData.form as Record<string, unknown>, formId)
  }

  if (!formHasRenderableLayout(documentData.form)) {
    if (rawXml) {
      await applyParsedForm(rawXml)
    } else {
      await applyParsedForm(documentData.form)
    }
  } else {
    stampFormIdOnPanels(documentData.form as Record<string, unknown>, formId)
  }

  if (rawXml && formLayoutHasStubPanels(documentData.form)) {
    await applyParsedForm(rawXml)
  }

  documentData.formId = formId
}

function stampFormIdOnPanels(form: Record<string, unknown>, formId: string): void {
  if (form.panels && typeof form.panels === 'object') {
    Object.keys(form.panels as Record<string, unknown>).forEach((panelId) => {
      const panel = (form.panels as Record<string, unknown>)[panelId]
      if (panel && typeof panel === 'object') {
        ;(panel as Record<string, unknown>).formId = formId
      }
    })
  }
  setFormIdOnPanels(form.main, formId)
  setFormIdOnPanels(form.header, formId)
  setFormIdOnPanels(form.footer, formId)
}

function setFormIdOnPanels(panels: unknown, formId: string): void {
  if (!panels) return
  if (Array.isArray(panels)) {
    panels.forEach((panel) => {
      if (panel && typeof panel === 'object') {
        const p = panel as Record<string, unknown>
        if (p.panelId) {
          p.formId = formId
        }
        if (p.panelRows) {
          setFormIdOnPanels(p.panelRows, formId)
        }
      }
    })
    return
  }
  if (typeof panels === 'object') {
    Object.keys(panels as Record<string, unknown>).forEach((key) => {
      const panel = (panels as Record<string, unknown>)[key]
      if (panel && typeof panel === 'object' && (panel as Record<string, unknown>).panelId) {
        ;(panel as Record<string, unknown>).formId = formId
      }
    })
  }
}

export async function buildMainFramePageDataFromActionResponse(
  response: Record<string, unknown>,
  options?: MainFrameBuildOptions,
): Promise<Record<string, unknown>> {
  let formId =
    options?.formIdHint ??
    resolveMainFrameFormId(response)

  const processedData = { ...(response as Record<string, unknown>) }
  await applyActionResponseFormLayout(processedData, formId)

  if (processedData.uniData) {
    const uni = processedData.uniData as Record<string, unknown>
    if (!uni.fields) {
      processedData.uniData = { fields: { fields: uni } }
    } else if (!(uni.fields as Record<string, unknown>).fields) {
      processedData.uniData = { fields: { fields: uni.fields } }
    }
  } else {
    processedData.uniData = {}
  }

  const platform = options?.platform
  if (platform) {
    const uniData = processedData.uniData as Record<string, unknown>
    uniData.clientData = {
      ...(uniData.clientData as Record<string, unknown> | undefined),
      platform,
    }
  }

  processedData.formId = formId
  const prepared = await prepareMainFrameDocumentData(processedData, {
    isStartupAction: options?.isStartupAction,
    openedFormId: formId,
  })
  if (!options?.appStore) {
    return prepared
  }
  const { ensureWebClientOnLoadBeforeOpenForm } = await import(
    '@/services/action/webClientFormOnLoadBeforeBuild'
  )
  return ensureWebClientOnLoadBeforeOpenForm(prepared, {
    appStore: options.appStore,
    openedFormId: formId,
    parseForm: false,
    dependencies: options.dependencies,
    invokeNestedElementActionFromClientScript: options.invokeNestedElementActionFromClientScript,
  })
}

export { MAIN_FRAME_SLOT_ID, resolveMainFramePageSlotId }
